import React, { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface UseTableSortOptions<T> {
  defaultField?: keyof T | string;
  defaultDirection?: SortDirection;
  customSort?: Record<string, (a: T, b: T, direction: SortDirection) => number>;
}

// Common date field candidates across SafetyCore models
const DATE_CANDIDATE_KEYS = [
  'event_date',
  'eventDate',
  'created_at',
  'createdAt',
  'date',
  'submission_date',
  'submissionDate',
  'target_completion_date',
  'targetCompletionDate',
  'target_date',
  'targetDate',
  'due_date',
  'dueDate',
  'due_at',
  'dueAt',
  'audit_date',
  'auditDate',
  'inspection_date',
  'inspectionDate',
  'published_at',
  'publishedAt',
  'linked_at',
  'linkedAt',
  'updated_at',
  'updatedAt',
  'start_date',
  'startDate',
  'end_date',
  'endDate',
  'review_date',
  'reviewDate',
];

// Common ID / Code field candidates across SafetyCore models
const ID_CANDIDATE_KEYS = [
  'id',
  'task_number',
  'report_number',
  'occurrence_number',
  'occurrenceNumber',
  'investigation_number',
  'investigationNumber',
  'action_number',
  'actionNumber',
  'hazard_number',
  'hazardNumber',
  'finding_number',
  'findingNumber',
  'audit_number',
  'auditNumber',
  'capa_number',
  'capaNumber',
  'inspection_number',
  'inspectionNumber',
  'moc_number',
  'mocNumber',
  'code',
  'document_number',
  'documentNumber',
  'number',
  'ref_number',
];

const PRIORITY_WEIGHTS: Record<string, number> = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  NORMAL: 2,
  LOW: 1,
};

function isDateKey(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    lower.includes('date') ||
    lower.endsWith('_at') ||
    lower.endsWith('at') ||
    DATE_CANDIDATE_KEYS.some((candidate) => candidate.toLowerCase() === lower)
  );
}

function isDateValue(val: any): boolean {
  if (!val || typeof val !== 'string') return false;
  // Match ISO 8601 or YYYY-MM-DD or standard datetime
  if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
    const timestamp = Date.parse(val);
    return !Number.isNaN(timestamp);
  }
  return false;
}

/**
 * Automatically inspects the items to select default sort field:
 * 1. Checks if any date field exists -> defaults to that date field in DESC order (newest first).
 * 2. If no date field exists -> defaults to candidate ID field in DESC order (latest ID first).
 */
export function detectDefaultSort<T>(items: T[]): { field: string; direction: SortDirection } {
  if (!items || items.length === 0) {
    return { field: 'id', direction: 'desc' };
  }

  const sample = items[0] as Record<string, any>;
  if (!sample || typeof sample !== 'object') {
    return { field: 'id', direction: 'desc' };
  }

  const keys = Object.keys(sample);

  // 1. Look for known date candidate keys
  for (const candidate of DATE_CANDIDATE_KEYS) {
    if (candidate in sample && sample[candidate] != null) {
      return { field: candidate, direction: 'desc' };
    }
  }

  // Look for any key that ends with Date or _at or contains date
  for (const k of keys) {
    if (isDateKey(k) && sample[k] != null) {
      return { field: k, direction: 'desc' };
    }
  }

  // Look for values that look like dates
  for (const k of keys) {
    if (isDateValue(sample[k])) {
      return { field: k, direction: 'desc' };
    }
  }

  // 2. If no date found, look for candidate ID keys -> newest/latest ID first (desc)
  for (const candidate of ID_CANDIDATE_KEYS) {
    if (candidate in sample && sample[candidate] != null) {
      return { field: candidate, direction: 'desc' };
    }
  }

  // Look for any key containing 'id' or 'number' or 'code'
  for (const k of keys) {
    const lower = k.toLowerCase();
    if ((lower.includes('id') || lower.includes('number') || lower.includes('code')) && sample[k] != null) {
      return { field: k, direction: 'desc' };
    }
  }

  // Fallback to first key
  return { field: keys[0] || 'id', direction: 'desc' };
}

export function useTableSort<T>(items: T[], options?: UseTableSortOptions<T>) {
  // Determine default sort field and direction
  const defaultSort = useMemo(() => {
    if (options?.defaultField) {
      const fieldStr = String(options.defaultField);
      const direction = options.defaultDirection || (isDateKey(fieldStr) ? 'desc' : 'desc');
      return { field: fieldStr, direction };
    }
    return detectDefaultSort(items);
  }, [items, options?.defaultField, options?.defaultDirection]);

  const [sortField, setSortField] = useState<string>(() => defaultSort.field);
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => defaultSort.direction);

  const requestSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // When switching to a date field, default to newest first (desc); otherwise asc
      setSortDirection(isDateKey(field) ? 'desc' : 'asc');
    }
  };

  const sortedItems = useMemo(() => {
    if (!items || items.length <= 1) return items || [];

    return [...items].sort((a: any, b: any) => {
      if (options?.customSort && options.customSort[sortField]) {
        return options.customSort[sortField](a, b, sortDirection);
      }

      const valA = a ? a[sortField] : undefined;
      const valB = b ? b[sortField] : undefined;

      // Handle null/undefined values: send to end
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      // Date comparison
      if (isDateKey(sortField) || (isDateValue(valA) && isDateValue(valB))) {
        const timeA = new Date(valA).getTime();
        const timeB = new Date(valB).getTime();
        if (!Number.isNaN(timeA) && !Number.isNaN(timeB)) {
          return sortDirection === 'asc' ? timeA - timeB : timeB - timeA;
        }
      }

      // Priority comparison
      if (typeof valA === 'string' && typeof valB === 'string') {
        const upA = valA.toUpperCase();
        const upB = valB.toUpperCase();
        if (PRIORITY_WEIGHTS[upA] !== undefined && PRIORITY_WEIGHTS[upB] !== undefined) {
          const diff = PRIORITY_WEIGHTS[upA] - PRIORITY_WEIGHTS[upB];
          return sortDirection === 'asc' ? diff : -diff;
        }
      }

      // Numeric comparison
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }

      // Natural alphanumeric string comparison (handles "OCR-2026-00082", "TSK-001", etc.)
      const strA = String(valA);
      const strB = String(valB);
      const cmp = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [items, sortField, sortDirection, options?.customSort]);

  const getSortIndicator = (field: string) => {
    if (sortField !== field) {
      return (
        <span
          style={{
            marginLeft: '4px',
            fontSize: '11px',
            color: '#94a3b8',
            opacity: 0.6,
            fontWeight: 400,
          }}
          aria-hidden="true"
        >
          ↕
        </span>
      );
    }
    return (
      <span
        style={{
          marginLeft: '4px',
          fontSize: '11px',
          color: '#2563eb',
          fontWeight: 700,
        }}
        aria-label={sortDirection === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
      >
        {sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const getSortHeaderProps = (field: string, title?: string) => ({
    onClick: () => requestSort(field),
    style: {
      cursor: 'pointer',
      userSelect: 'none' as const,
      backgroundColor: sortField === field ? 'rgba(37, 99, 235, 0.05)' : undefined,
    },
    title: title || `Click to sort by ${field}`,
  });

  return {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
    getSortIndicator,
    getSortHeaderProps,
  };
}

/**
 * Reusable Sortable Table Header Component (<SortableTh>)
 */
export const SortableTh: React.FC<{
  field: string;
  sortField?: string;
  sortDirection?: SortDirection;
  onSort?: (field: string) => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  align?: 'left' | 'center' | 'right';
  title?: string;
}> = ({
  field,
  sortField,
  sortDirection,
  onSort,
  children,
  style,
  className,
  align = 'left',
  title,
}) => {
  const isCurrent = sortField === field;
  return (
    <th
      className={className}
      onClick={() => onSort?.(field)}
      style={{
        cursor: onSort ? 'pointer' : 'default',
        userSelect: 'none',
        textAlign: align,
        backgroundColor: isCurrent ? 'rgba(37, 99, 235, 0.05)' : undefined,
        ...style,
      }}
      title={title || (onSort ? `Click to sort by ${field}` : undefined)}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
          width: '100%',
        }}
      >
        <span>{children}</span>
        {onSort && (
          <span
            style={{
              fontSize: '11px',
              color: isCurrent ? '#2563eb' : '#94a3b8',
              fontWeight: isCurrent ? 700 : 400,
              opacity: isCurrent ? 1 : 0.6,
            }}
          >
            {isCurrent ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        )}
      </div>
    </th>
  );
};
